import { useMemo, useCallback } from 'react';

/**
 * Report Data Adapters Hook
 *
 * Converts useSpringPageQuery return values to format expected by old JSX components.
 * Eliminates 90 lines of duplicated adapter code (15 lines × 6 pages).
 *
 * This is a temporary bridge layer during architectural migration.
 * Once all JSX components are migrated to TypeScript, this hook can be removed.
 *
 * Purpose:
 * - New TS hooks return { currentPage, totalPages, totalElements, pageSize, changePageSize }
 * - Old JSX components expect { pagination: { pageNumber, ... }, params: { size }, changeParams }
 * - This hook bridges the gap without modifying old components
 *
 * Usage:
 * ```tsx
 * const report = useSchoolsReport(); // Returns useSpringPageQuery interface
 * const adapters = useReportDataAdapters(report); // Converts to old format
 *
 * <ReportDetails pagination={adapters.pagination} />
 * ```
 *
 * ⚠️ `params` si `changeParams` erau pentru `SizeSelector`, sters in faza 3.0
 * (2026-09-03) fiindca n-avea niciun consumator. Marimea paginii se schimba azi
 * din `SpringPagination`, direct prin `report.changePageSize`. Cele doua campuri
 * raman in interfata pentru ca nu le-am putut demonstra moarte fara sa ating
 * consumatorii lui `useReportDataAdapters` din ambele jumatati — de curatat cand
 * se atinge urmatoarea data hook-ul.
 */

interface SpringPageReport {
    /** Current page number (0-indexed) */
    currentPage: number;

    /** Total number of pages */
    totalPages: number;

    /** Total number of elements across all pages */
    totalElements: number;

    /** Current page size */
    pageSize: number;

    /** Change page size (resets to page 0) */
    changePageSize: (size: number) => void;
}

interface AdaptedData {
    /** Pagination data in old JSX format */
    pagination: {
        pageNumber: number;
        totalPages: number;
        totalElements: number;
        pageSize: number;
    };

    /** Params object — fostul `SizeSelector` (sters 2026-09-03) */
    params: {
        size: string; // Old component expects string
    };

    /** Change handler — fostul `SizeSelector` (sters 2026-09-03) */
    changeParams: (event: { target: { name: string; value: string } }, type: string, shouldRefetch: boolean) => void;
}

/**
 * Adapts new TypeScript report interface to old JSX component interface
 *
 * @param report - Object returned from useSpringPageQuery
 * @returns Adapted data for old JSX components (ReportDetails)
 *
 * @example
 * const report = useUsersReport();
 * const { pagination, params, changeParams } = useReportDataAdapters(report);
 */
export function useReportDataAdapters(report: SpringPageReport): AdaptedData {
    // Adapt pagination format
    const pagination = useMemo(() => ({
        pageNumber: report.currentPage,
        totalPages: report.totalPages,
        totalElements: report.totalElements,
        pageSize: report.pageSize
    }), [report.currentPage, report.totalPages, report.totalElements, report.pageSize]);

    // Adapt params format ({ size: string })
    const params = useMemo(() => ({
        size: String(report.pageSize)
    }), [report.pageSize]);

    // Adapt changeParams handler (event cu target.name si target.value)
    const changeParams = useCallback((event: { target: { name: string; value: string } }, _type: string, _shouldRefetch: boolean) => {
        if (event.target.name === 'size') {
            report.changePageSize(Number(event.target.value));
        }
    }, [report.changePageSize]);

    return {
        pagination,
        params,
        changeParams
    };
}
